import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useVoiceCommands } from '../utils/hooks/useVoiceCommands';
import { fetchAuthSession } from 'aws-amplify/auth';

const parseList = (text, prefix) => {
  if (!text) return [];
  const content = text.replace(prefix, '').trim();
  if (content.startsWith('None') || content.includes('not demonstrated')) {
    return ['None demonstrated'];
  }
  return content.split(/\d+\.\s+/)
    .filter(item => item.trim().length > 0)
    .map(item => item.trim());
};

const parseFillerWords = (text) => {
  if (!text) return [];
  const content = text.replace('FILLER_WORDS:', '').trim();
  if (content.startsWith('None') || content.includes('not found')) {
    return [];
  }
  return content.split(',').map(word => word.trim());
};

const StatusBadge = ({ state, status }) => {
  const getColor = () => {
    if (state === 'in_progress') return 'bg-blue-100 text-blue-800';
    if (status === 'active') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getColor()}`}>
      {state === 'in_progress' ? 'In Progress' : state}
    </span>
  );
};

const InterviewResults = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');
  const API_URL = process.env.REACT_APP_API_GATEWAY_URL;
  const [selectedTopics, setSelectedTopics] = useState({
    systemDesign: false,
    behavioral: false
  });
  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs for scrolling
  const strengthsRef = useRef(null);
  const improvementsRef = useRef(null);
  const questionsRef = useRef(null);
  const scoreRef = useRef(null);

  const questions = interviewData ? parseList(interviewData.questions, 'QUESTIONS:') : [];
  const strengths = interviewData ? parseList(interviewData.strengths, 'STRENGTHS:') : [];
  const improvements = interviewData ? parseList(interviewData.areasForImprovement, 'IMPROVEMENTS:') : [];

  const handleAddToStudyPlan = () => {
    const selectedItems = [];
    if (selectedTopics.systemDesign) selectedItems.push('System Design');
    if (selectedTopics.behavioral) selectedItems.push('Behavioral Interview');
    
    if (selectedItems.length === 0) {
      return 'Please select at least one topic to add to your study plan';
    }

    return `Added ${selectedItems.join(' and ')} to your study plan`;
  };

  const voiceCommands = {
    'show strengths': () => {
      strengthsRef.current?.scrollIntoView({ behavior: 'smooth' });
      return 'Here are your strengths. You can ask for improvements or questions.';
    },
    'show improvements': () => {
      improvementsRef.current?.scrollIntoView({ behavior: 'smooth' });
      return 'Here are your areas for improvement. You can ask for strengths or questions.';
    },
    'show questions': () => {
      questionsRef.current?.scrollIntoView({ behavior: 'smooth' });
      return 'Here are the questions covered in your interview. You can ask for strengths or improvements.';
    },
    'select system design': () => {
      setSelectedTopics(prev => ({ ...prev, systemDesign: true }));
      return 'System Design selected';
    },
    'select behavioral': () => {
      setSelectedTopics(prev => ({ ...prev, behavioral: true }));
      return 'Behavioral Interview selected';
    },
    'deselect system design': () => {
      setSelectedTopics(prev => ({ ...prev, systemDesign: false }));
      return 'System Design deselected';
    },
    'deselect behavioral': () => {
      setSelectedTopics(prev => ({ ...prev, behavioral: false }));
      return 'Behavioral Interview deselected';
    },
    'add selected items': () => {
      return handleAddToStudyPlan();
    }
  };

  const { isListening, errorMessage, toggleListening, speak } = useVoiceCommands({
    commands: voiceCommands,
    onError: (error) => setError('Voice command error occurred')
  });

  // Fetch interview data
  useEffect(() => {
    const fetchInterviewData = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        if (!token) {
          setError('No authentication token found');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/history/interview?id=${sessionId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
            'Access-Control-Allow-Methods': 'OPTIONS,GET'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch interview results`);
        }

        const data = await response.json();
        setInterviewData(data);
      } catch (err) {
        setError('Failed to load interview results');
      } finally {
        setLoading(false);
      }
    };

    fetchInterviewData();
  }, [sessionId, API_URL]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Loading interview results...</div>
        </div>
      </div>
    );
  }

  if (error || !interviewData) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
            <div className="text-lg text-red-600">
              {error || 'Failed to load interview results'}
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Floating Voice Command Button */}
      <div className="fixed bottom-8 right-8 flex flex-col items-center">
        <button
          onClick={toggleListening}
          className={`p-4 rounded-full shadow-lg transition-all relative ${
            isListening ? 'bg-red-500' : 'bg-gray-300'
          } hover:opacity-90 z-50`}
          title={isListening ? 'Stop listening' : 'Start voice commands'}
        >
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
            />
          </svg>
          {!isListening && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-0.5 bg-red-500 transform rotate-45"></div>
            </div>
          )}
        </button>
        <span className="text-xs mt-2 text-gray-600 font-medium">
          {isListening ? 'Listening...' : 'Not listening'}
        </span>
        {isListening && (
          <div className="absolute bottom-full mb-2 p-2 bg-blue-100 text-blue-800 rounded-lg text-sm max-w-xs">
            <div className="font-semibold mb-1">Voice Commands:</div>
            <div className="space-y-1 text-xs">
              <div>• show strengths</div>
              <div>• show improvements</div>
              <div>• show questions</div>
              <div>• select system design</div>
              <div>• select behavioral</div>
              <div>• deselect system design</div>
              <div>• deselect behavioral</div>
              <div>• add selected items</div>
            </div>
          </div>
        )}
        {errorMessage && (
          <div className="absolute bottom-full mb-2 p-2 bg-red-100 text-red-700 rounded-lg text-sm max-w-xs text-center">
            {errorMessage}
          </div>
        )}
      </div>

      {/* Header Section */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {interviewData.interviewType === 'technical' ? 'Technical Interview' : 
                 interviewData.interviewType === 'behavioral' ? 'Behavioral Interview' :
                 interviewData.interviewType === 'system design' ? 'System Design Interview' :
                 interviewData.interviewType === 'coding' ? 'Coding Interview' :
                 interviewData.interviewType.charAt(0).toUpperCase() + interviewData.interviewType.slice(1) + ' Interview'}
              </h1>
              <StatusBadge state={interviewData.state} status={interviewData.status} />
            </div>
            <div className="space-y-1">
              <p className="text-gray-600">
                <span className="font-semibold">Position:</span> {interviewData.position}
              </p>
              <p className="text-gray-600">
                <span className="font-semibold">Company:</span> {interviewData.company}
              </p>
              <p className="text-gray-600">
                <span className="font-semibold">Date:</span> {new Date(parseInt(interviewData.date) * 1000).toISOString().split('T')[0]}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Questions Section */}
      <div ref={questionsRef} className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Questions Covered</h2>
        <div className="space-y-6">
          {questions.map((question, index) => (
            <div key={index} className="border-b border-gray-200 last:border-0 pb-6 last:pb-0">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-medium text-gray-900">{question}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strengths & Improvements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Strengths Section */}
        <div ref={strengthsRef} className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            <span className="text-green-600 mr-2">✓</span>
            Strengths
          </h2>
          <ul className="space-y-3">
            {strengths.map((strength, index) => (
              <li key={index} className="flex items-start">
                <span className="text-green-500 mr-2">•</span>
                <span className="text-gray-700">{strength}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Areas for Improvement Section */}
        <div ref={improvementsRef} className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            <span className="text-blue-600 mr-2">↗</span>
            Areas for Improvement
          </h2>
          <ul className="space-y-3">
            {improvements.map((area, index) => (
              <li key={index} className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span className="text-gray-700">{area}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Filler Words Section */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">
          <span className="text-yellow-600 mr-2">⚠</span>
          Filler Words & Repetitions
        </h2>
        {parseFillerWords(interviewData.fillerWords).length > 0 ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {parseFillerWords(interviewData.fillerWords).map((word, index) => (
                <span 
                  key={index} 
                  className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium"
                >
                  {word}
                </span>
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Consider reducing the use of these words to make your responses more concise and professional.
            </p>
          </div>
        ) : (
          <p className="text-gray-600">No significant filler words or repetitions detected in your responses.</p>
        )}
      </div>

      {/* Study Plan Section */}
      {/* Temporarily hidden
      <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">
          Add to Study Plan
        </h2>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="systemDesign"
              checked={selectedTopics.systemDesign}
              onChange={(e) => setSelectedTopics(prev => ({ ...prev, systemDesign: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="systemDesign" className="ml-2 block text-sm text-gray-900">
              System Design Interview Practice
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="behavioral"
              checked={selectedTopics.behavioral}
              onChange={(e) => setSelectedTopics(prev => ({ ...prev, behavioral: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="behavioral" className="ml-2 block text-sm text-gray-900">
              Behavioral Interview Practice
            </label>
          </div>
          <button
            onClick={() => {
              const message = handleAddToStudyPlan();
              speak(message);
            }}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add to Study Plan
          </button>
        </div>
      </div>
      */}
    </div>
  );
};

export default InterviewResults; 